import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  ElementRef,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { AccountService } from '@core/providers/account/account.service';
import { ContextService } from '@core/providers/context/context.service';
import { ContextUtil } from '@core/utils';
import { DefaultAvatarComponent } from '@shared/components/default-avatar/default-avatar.component';
import { HighlightedTextComponent } from '@shared/components/highlighted-text/highlighted-text.component';
import { TooltipWhenTruncatedDirective } from '@shared/directives/tooltip-when-truncated.directive';
import { type ContextObject } from '@shared/interfaces/login-object';
import { HighlightPipe } from '@shared/pipes/highlight.pipe';
import { matchesSearch, toSearchQuery } from '@shared/utils/search-text';
import { NavModelService } from '../navigation/nav-model.service';
import {
  type SwitcherAccount,
  type SwitcherCurrentContext,
  type SwitcherVenue,
  type SwitcherVenueList,
} from './context-switcher.model';
import { ContextSwitcherService, MIN_VENUE_SEARCH_LENGTH } from './context-switcher.service';

const DROPDOWN_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 10 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -10 },
];

/** Keeps the cross-account venue search off the wire while the user is still typing. */
const SEARCH_DEBOUNCE_MS = 250;

/** Placeholder rows shown while a venue list is in flight. */
const LOADING_PLACEHOLDER_ROWS = [0, 1, 2];

const EMPTY_VENUE_LIST: SwitcherVenueList = { isLoading: false, venues: [] };
const EMPTY_VENUE_LIST_LOADING: SwitcherVenueList = { isLoading: true, venues: [] };

/** A keystroke meant for the search box rather than for the row it was pressed on. */
function isTypingKey(event: KeyboardEvent): boolean {
  const isCharacter = event.key.length === 1 && event.key !== ' ';

  return (
    (isCharacter || event.key === 'Backspace') && !event.ctrlKey && !event.metaKey && !event.altKey
  );
}

@Component({
  selector: 'app-context-switcher',
  templateUrl: './context-switcher.component.html',
  styleUrls: ['./context-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    OverlayModule,
    MatDividerModule,
    MatIcon,
    MatProgressSpinner,
    DefaultAvatarComponent,
    HighlightedTextComponent,
    TooltipWhenTruncatedDirective,
    HighlightPipe,
    TranslatePipe,
  ],
  host: {
    '(document:keydown.escape)': 'close(true)',
  },
})
export class ContextSwitcherComponent {
  private readonly switcher = inject(ContextSwitcherService);
  private readonly contextService = inject(ContextService);
  private readonly accountService = inject(AccountService);
  private readonly contextUtil = inject(ContextUtil);
  private readonly navModel = inject(NavModelService);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerButton');
  private readonly panelBody = viewChild<ElementRef<HTMLElement>>('panelBody');

  readonly isOpen = signal(false);
  readonly drilledAccount = signal<SwitcherAccount | null>(null);
  readonly query = signal('');

  /** As typed, for the server, which applies its own matching rule. */
  private readonly typedQuery = computed(() => this.query().trim());
  readonly normalizedQuery = computed(() => toSearchQuery(this.query()));
  readonly isSearching = computed(() => this.normalizedQuery().length > 0);

  /**
   * Owned by the service, so the trigger shows progress whether the switch was started here or
   * from the search palette.
   */
  readonly switchingTarget = this.switcher.switchingTarget;
  readonly isSwitching = this.switcher.isSwitching;

  readonly dropdownPositions = DROPDOWN_POSITIONS;

  private readonly context = toSignal<ContextObject | null>(
    this.contextService.getContext$().pipe(
      startWith(null),
      catchError(() => of(null))
    ),
    { initialValue: null }
  );

  /**
   * Fetched once up front — `toggle` needs the list to open on the current venue's account — and
   * again on every open, so a request that failed the first time is retried instead of leaving
   * the panel permanently empty. Successful lookups are cached by the permissions service, so
   * reopening costs nothing.
   */
  readonly accounts = toSignal(
    toObservable(this.isOpen).pipe(
      filter(isOpen => isOpen),
      startWith(true),
      switchMap(() => this.switcher.getAccounts$())
    ),
    { initialValue: [] as ReadonlyArray<SwitcherAccount> }
  );

  /** The account owning the current context — the only source of its uuid in venue context. */
  private readonly currentAccountUuid = toSignal(
    this.accountService.getAccountContext$().pipe(
      map(account => account?.uuid ?? null),
      startWith(null),
      catchError(() => of(null))
    ),
    { initialValue: null }
  );

  readonly isTopLevelDashboardAllowed = toSignal(
    this.switcher.isTopLevelDashboardAllowed$().pipe(startWith(false)),
    { initialValue: false }
  );

  private readonly drilledVenues = toSignal(
    toObservable(this.drilledAccount).pipe(
      switchMap(account =>
        account
          ? this.switcher.getVenues$(account).pipe(
              map(venues => ({ isLoading: false, venues })),
              startWith(EMPTY_VENUE_LIST_LOADING)
            )
          : of(EMPTY_VENUE_LIST)
      )
    ),
    { initialValue: EMPTY_VENUE_LIST }
  );

  /**
   * Cross-account venue matches. It runs inside a drilled account too, where the account's own
   * venues are already in memory and filtered locally: the search is what reaches everything
   * outside the account, so that a query aimed elsewhere is not a dead end.
   */
  private readonly searchedVenues = toSignal(
    toObservable(this.typedQuery).pipe(
      distinctUntilChanged(),
      switchMap(search =>
        search.length >= MIN_VENUE_SEARCH_LENGTH
          ? // The debounce is a `timer` inside the projection rather than `debounceTime` ahead of
            // it, so the loading state covers the wait as well as the request. With the delay
            // outside, the previous query's rows stayed put and "No results" flashed in the gap.
            timer(SEARCH_DEBOUNCE_MS).pipe(
              switchMap(() => this.switcher.searchVenues$(search)),
              map(venues => ({ isLoading: false, venues })),
              startWith(EMPTY_VENUE_LIST_LOADING)
            )
          : of(EMPTY_VENUE_LIST)
      )
    ),
    { initialValue: EMPTY_VENUE_LIST }
  );

  readonly currentContext = computed<SwitcherCurrentContext | null>(() => {
    // Out of context the trigger says so rather than naming the account you happen to be able to
    // return to — on the top-level dashboard you are looking across all of them. The wording is
    // left to the template so the translate pipe keeps it current when the language changes.
    if (this.navModel.isOutOfContext()) {
      return { kind: 'topLevelDashboard', uuid: null, name: '', subtitle: '' };
    }

    const context = this.context();
    if (!context?.type) {
      return null;
    }

    if (this.contextUtil.isAccountContext(context.type)) {
      // The subtitle carries the account/venue indicator, so it holds no extra detail here.
      return {
        kind: 'account',
        uuid: context.uuid ?? null,
        name: context.brandName || context.accountName || '',
        subtitle: '',
      };
    }

    return {
      kind: 'venue',
      uuid: context.uuid ?? null,
      name: context.venueName ?? '',
      subtitle: context.accountName ?? '',
    };
  });

  /**
   * What the trigger shows. During a switch that is the destination rather than the context
   * still loaded behind it, so the header reflects the choice the moment it is made.
   */
  readonly displayedContext = computed(() => this.switchingTarget() ?? this.currentContext());

  private readonly matchingAccounts = computed(() => {
    const query = this.normalizedQuery();
    const accounts = this.accounts();

    return query ? accounts.filter(account => matchesSearch(account.name, query)) : accounts;
  });

  /**
   * The accounts the list offers: every reachable one at the top level, and inside a drilled
   * account the matches outside it — which only a search asks for.
   */
  readonly visibleAccounts = computed<ReadonlyArray<SwitcherAccount>>(() => {
    const drilled = this.drilledAccount();

    if (!drilled) {
      return this.matchingAccounts();
    }

    return this.isSearching()
      ? this.matchingAccounts().filter(account => account.uuid !== drilled.uuid)
      : [];
  });

  readonly visibleVenues = computed<ReadonlyArray<SwitcherVenue>>(() => {
    const query = this.normalizedQuery();

    if (this.drilledAccount()) {
      const venues = this.drilledVenues().venues;
      return query ? venues.filter(venue => matchesSearch(venue.name, query)) : venues;
    }

    return query ? this.searchedVenues().venues : [];
  });

  /**
   * Whether the query names the account being browsed. It is kept out of the accounts offered
   * below — "other accounts" is what that group is — so its own row is what has to answer for it.
   */
  readonly queryNamesDrilledAccount = computed(() => {
    const drilled = this.drilledAccount();

    return drilled !== null && this.matchingAccounts().some(match => match.uuid === drilled.uuid);
  });

  /** Matches outside the drilled account, whose own venues the list above already holds. */
  readonly otherAccountVenues = computed<ReadonlyArray<SwitcherVenue>>(() => {
    const drilled = this.drilledAccount();

    if (!drilled || !this.isSearching()) {
      return [];
    }

    return this.searchedVenues().venues.filter(venue => venue.accountUuid !== drilled.uuid);
  });

  readonly isLoadingVenues = computed(() =>
    this.drilledAccount() ? this.drilledVenues().isLoading : this.searchedVenues().isLoading
  );

  /** Inside a drilled account the cross-account search feeds only the group below the venues. */
  readonly isLoadingOtherAccounts = computed(
    () => this.drilledAccount() !== null && this.isSearching() && this.searchedVenues().isLoading
  );

  readonly showsOtherAccountsGroup = computed(
    () =>
      this.drilledAccount() !== null &&
      this.isSearching() &&
      (this.isLoadingOtherAccounts() ||
        this.visibleAccounts().length > 0 ||
        this.otherAccountVenues().length > 0)
  );

  /**
   * Heads the venue list whenever something else shares the panel with it — its own group at the
   * top level, and the account being browsed when matches from outside follow underneath. On its
   * own it needs no label: the panel head already says which account these belong to.
   */
  readonly venuesLabelKey = computed<string | null>(() => {
    if (!this.isSearching() || this.visibleVenues().length === 0) {
      return null;
    }

    if (!this.drilledAccount()) {
      return 'SHARED.SYSTEM.UNITS';
    }

    return this.showsOtherAccountsGroup() ? 'SHARED.SYSTEM.IN_THIS_ACCOUNT' : null;
  });

  readonly hasNoResults = computed(
    () =>
      this.isSearching() &&
      !this.isLoadingVenues() &&
      !this.isLoadingOtherAccounts() &&
      this.visibleAccounts().length === 0 &&
      this.visibleVenues().length === 0 &&
      this.otherAccountVenues().length === 0
  );

  /**
   * "venue" / "room" / "product", per the account's configured children type. The lookup reads
   * storage rather than a signal, so the context is read first to give this a producer — without
   * it the value was memoised for the component's lifetime and never followed a switch.
   */
  readonly accountChildrenName = computed(() => {
    this.context();

    return this.contextService.getAccountChildrenNameTranslation(true);
  });

  readonly loadingPlaceholderRows = LOADING_PLACEHOLDER_ROWS;

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        // The panel renders in an overlay, so focus has to wait for it to attach.
        setTimeout(() => this.searchInput()?.nativeElement.focus());
      }
    });
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close(true);
      return;
    }

    // Whichever context you are in, the likely next destination is one of its siblings, so the
    // panel opens on the current account's venues rather than making you drill in again. The back
    // button is one step away when the destination is another account entirely.
    this.drilledAccount.set(this.accountOfCurrentContext());
    this.query.set('');
    this.isOpen.set(true);
  }

  /**
   * `restoreFocus` for the paths where focus is inside the panel that is about to disappear —
   * Escape, and picking a row — since tearing down an overlay leaves focus on `<body>`. Not for
   * an outside click, which would steal focus from whatever the user just clicked.
   */
  close(restoreFocus = false): void {
    this.isOpen.set(false);

    if (restoreFocus) {
      this.triggerButton()?.nativeElement.focus();
    }
  }

  onQueryInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  /**
   * The rows the arrow keys walk, read from the panel rather than tracked per row: the DOM is
   * already the one place that holds them in painted order, across several blocks whose
   * composition changes with the query. Disabled rows report where you are and are not
   * destinations, so they are skipped; the drill-down controls stay on the Tab order alone.
   */
  private focusableRows(): ReadonlyArray<HTMLButtonElement> {
    const body = this.panelBody()?.nativeElement;

    return body
      ? Array.from(body.querySelectorAll<HTMLButtonElement>('.fb-switcher__row:not(:disabled)'))
      : [];
  }

  /**
   * Down/Up enter the list from either end. Enter takes the first row, which is the top match
   * for what was typed — never a stale one, since a query in flight clears the rows it feeds
   * and leaves only locally filtered ones behind.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    const rows = this.focusableRows();

    if (rows.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      rows[0].focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      rows[rows.length - 1].focus();
      return;
    }

    if (event.key === 'Enter' && this.isSearching()) {
      event.preventDefault();
      rows[0].click();
    }
  }

  /**
   * Moves focus between rows, and hands it back to the search box on Up from the first one and
   * on any character — so refining the query never needs the pointer. Enter and Space are left
   * to the button itself.
   */
  onBodyKeydown(event: KeyboardEvent): void {
    const rows = this.focusableRows();

    if (rows.length === 0) {
      return;
    }

    const row = this.rowOf(event.target as HTMLElement);
    const index = row ? rows.indexOf(row) : -1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        rows[(index + 1) % rows.length].focus();
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (index <= 0) {
          this.searchInput()?.nativeElement.focus();
          return;
        }
        rows[index - 1].focus();
        return;
      case 'Home':
        event.preventDefault();
        rows[0].focus();
        return;
      case 'End':
        event.preventDefault();
        rows[rows.length - 1].focus();
        return;
      default:
        if (isTypingKey(event)) {
          // Deliberately not prevented: the character lands in the box that just took focus.
          this.searchInput()?.nativeElement.focus();
        }
    }
  }

  /** The row an event belongs to — the row itself, or the one the drill-down control sits beside. */
  private rowOf(target: HTMLElement): HTMLButtonElement | null {
    return (
      target.closest<HTMLButtonElement>('.fb-switcher__row') ??
      target
        .closest('.fb-switcher__account')
        ?.querySelector<HTMLButtonElement>('.fb-switcher__row') ??
      null
    );
  }

  drillInto(account: SwitcherAccount, event?: MouseEvent): void {
    // The drill-down control sits inside the account row; drilling must not select the account.
    event?.stopPropagation();
    this.query.set('');
    this.drilledAccount.set(account);
  }

  leaveDrilldown(): void {
    this.query.set('');
    this.drilledAccount.set(null);
  }

  selectAccount(account: SwitcherAccount): void {
    if (!account.canUse) {
      // Without ACCOUNT_USE the account itself is not a destination — show its venues instead.
      this.drillInto(account);
      return;
    }

    this.startSwitch({
      kind: 'account',
      uuid: account.uuid,
      name: account.name,
      subtitle: '',
    });
  }

  selectVenue(venue: SwitcherVenue): void {
    this.startSwitch({
      kind: 'venue',
      uuid: venue.uuid,
      name: venue.name,
      subtitle: venue.accountName,
    });
  }

  /**
   * Closes the panel and hands the switch to the service, whose `switchingTarget` the trigger
   * renders until the new context has loaded and navigation has happened.
   */
  private startSwitch(target: SwitcherCurrentContext): void {
    this.switcher.switchTo(target);
    this.close(true);
  }

  selectTopLevelDashboard(): void {
    this.close(true);
    this.switcher.goToTopLevelDashboard();
  }

  /**
   * The account the current context belongs to: itself in account context, its parent in venue
   * context, and none out of context — where there is no account to open on, so the panel starts
   * at the list. Null too when the account is not in the reachable list, which leaves the list as
   * the sensible fallback.
   */
  private accountOfCurrentContext(): SwitcherAccount | null {
    const current = this.currentContext();

    if (current?.kind === 'account') {
      return this.accounts().find(account => account.uuid === current.uuid) ?? null;
    }

    if (current?.kind !== 'venue') {
      return null;
    }

    const accountUuid = this.currentAccountUuid();

    return this.accounts().find(account => account.uuid === accountUuid) ?? null;
  }

  isCurrentAccount(account: SwitcherAccount): boolean {
    const current = this.currentContext();
    return current?.kind === 'account' && current.uuid === account.uuid;
  }

  isCurrentTopLevelDashboard(): boolean {
    return this.currentContext()?.kind === 'topLevelDashboard';
  }

  /**
   * The drilled account's row is an action, not an entity — the account is already named in the
   * head above it. So it says what activating it would do: nothing, when this is already the
   * context; step back up, from one of its venues; otherwise pick it.
   */
  readonly accountRowLabelKey = computed(() => {
    const drilled = this.drilledAccount();

    if (drilled && this.isCurrentAccount(drilled)) {
      return 'SHARED.SYSTEM.CURRENT_ACCOUNT';
    }

    return this.currentContext()?.kind === 'venue'
      ? 'SHARED.SYSTEM.BACK_TO_ACCOUNT'
      : 'SHARED.SYSTEM.SELECT_ACCOUNT';
  });

  isCurrentVenue(venue: SwitcherVenue): boolean {
    const current = this.currentContext();
    return current?.kind === 'venue' && current.uuid === venue.uuid;
  }

  /** Only a venue from outside the drilled account needs naming it; inside, the head does. */
  showsAccountName(venue: SwitcherVenue): boolean {
    return venue.accountUuid !== this.drilledAccount()?.uuid;
  }
}
